import {
	Component,
	Ref,
	Vue,
} from 'vue-property-decorator';
import { saveAs } from 'file-saver';
import * as DB from 'interfaces/database';
import { PickerOptions, PickerResponse } from 'filestack-js';
import { BModal } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import mitt from 'mitt';
import { TabEvent } from 'interfaces/app';
import { httpClient } from 'utils/http';
import 'vue-loading-overlay/dist/vue-loading.css';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.TemplateModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
})
export default class TemplateTable extends Vue.extend(Template) {
	@Ref('template-modal')
	private readonly modal!: BModal;

	@Ref('import-modal')
	private readonly importModal!: BModal;

	@Ref('templateTable')
	private readonly tableReference!: HTMLDivElement;

	protected isLoading = false;

	private offering: DB.OfferingModel[] = [];

	private language: DB.LanguageModel[] = [];

	private selectedRows: DB.TemplateModel[] = [];

	private placeHolders = {
		icon: 'Choose a file',
	}

	private search = '';

	private id = '';

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private importCatalogData = {
		id: [] as number[],
		offering: {} as DB.OfferingModel,
	}

	private addTemplateData = {} as Pick<DB.TemplateModel, 'name'>;

	private isLoaded = false;

	private externalId = '';

	private get loggedIn(): boolean {
		return this.$auth.isAuthenticated;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				title: 'ID',
				field: 'id',
			},
			{
				field: 'name',
				title: 'Name',
				headerFilter: true,
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.TemplateModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					instance.$mount();

					return (instance.$el as HTMLElement);
				},
			},
		];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteTemplate,
		);
		eventBus.on(
			'editItem',
			this.editTemplate,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeDestroy(): void {
		eventBus.off(
			'deleteItem',
			this.deleteTemplate,
		);
		eventBus.off(
			'editItem',
			this.editTemplate,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.off(
			'rowSelectionChanged',
			this.onSelectionChanged,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData().then(() => this.table?.on(
			'rowSelectionChanged',
			this.onSelectionChanged,
		)).catch((err) => {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		});
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
				selectable: true,
				// create a context menu to open row on a new tab
				rowContextMenu: [
					{
						label: 'Open in new tab',
						action: (e, row) => {
							const data = row.getData();
							this.$emit(
								'openInNewTab',
								data.id,
							);
						},
					},
				],
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});

		if (this.importCatalogData.id.length > 0) {
			parameter.append(
				'where',
				JSON.stringify({ id: this.importCatalogData.id }),
			);
		}
		this.table?.alert('Loading');
		try {
			const [templateData, offeringData, languageData] = await Promise.all([
				httpClient.get<DB.TemplateModel[]>(`/api/template?${parameter}`),
				httpClient.get<DB.OfferingModel[]>('/api/offering?limit=0'),
				httpClient.get<DB.LanguageModel[]>('/api/language?limit=0'),
			]);
			this.offering = offeringData.data;
			this.language = languageData.data;
			this.table?.setData(templateData.data);
		} catch (err: any) {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		} finally {
			this.importModal.hide();
			this.table?.clearAlert();
		}
	}

	private onSelectionChanged(): void {
		if (this.table) {
			this.selectedRows = this.table.getSelectedData();
		}
	}

	protected exportSelected(): void {
		const jsonString = JSON.stringify(this.selectedRows);
		const blob = new Blob(
			[jsonString],
			{ type: 'application/json' },
		);
		saveAs(
			blob,
			'Data.json',
		);
	}

	protected async exportAll(): Promise<void> {
		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get('/api/template?limit=0');
			const jsonString = JSON.stringify(data);
			const blob = new Blob(
				[jsonString],
				{ type: 'application/json' },
			);
			saveAs(
				blob,
				'Data.json',
			);
		} catch (error: any) {
			this.$bvToast.toast(
				`${error.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addTemplate(): void {
		this.isLoaded = true;
		httpClient.post(
			'/api/template',
			{
				name: this.addTemplateData.name,
			},
		).then((res) => {
			this.$bvToast.toast(
				'Template Added',
				{
					solid: true,
					variant: 'success',
				},
			);
			this.$emit(
				'routeTemplate',
				res.data.id,
			);

			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$bvToast.toast(
					`${err.message}`,
					{
						solid: true,
						variant: 'danger',
					},
				);
			});
	}

	protected uploadFile(): void {
		const options: PickerOptions = {
			fromSources: ['local_file_system'],
			onUploadDone: async (files: PickerResponse) => {
				this.placeHolders.icon = files.filesUploaded[0].filename;
				const { data } = await httpClient.post<DB.TemplateModel[]>(
					'/api/template/import',
					{
						url: files.filesUploaded[0].url,
					},
				);
				this.importCatalogData.id = data.map((item: DB.TemplateModel) => item.id);
				this.getData();
			},
		};
		this.$client.picker(options).open();
	}

	private editTemplate(data: TabEvent<DB.TemplateModel>['editItem']): void {
		this.$emit(
			'routeTemplate',
			data.id,
		);
	}

	private deleteTemplate(data: TabEvent<DB.TemplateModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading..');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/template/${data.id}`).then(() => {
					this.$bvToast.toast(
						'Item Deleted',
						{
							solid: true,
							variant: 'success',
						},
					);
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$bvToast.toast(
						`${err.message}`,
						{
							solid: true,
							variant: 'danger',
						},
					);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$bvToast.toast(
				`${err.message}`,
				{
					solid: true,
					variant: 'danger',
				},
			);
		});
	}
}
